'use client';

import React from 'react';

const quickLinks = ['Services', 'Packages', 'Before & After', 'Reviews', 'Contact'];
const serviceAreas = ['Rio Rancho', 'Albuquerque', 'Bernalillo', 'Corrales', 'Placitas', 'Los Ranchos'];

export default function RGPSFooter() {
  const scrollTo = (href: string) => {
    const map: Record<string, string> = {
      'Services': '#services',
      'Packages': '#packages',
      'Before & After': '#gallery',
      'Reviews': '#reviews',
      'Contact': '#estimate',
    };
    const el = document.querySelector(map[href] || '#home');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-dark-text text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-teal flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="9 22 9 12 15 12 15 22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <div className="font-bold text-sm leading-tight">Rio Grande</div>
                <div className="text-teal text-xs font-medium">Property Solutions</div>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Professional exterior cleaning services throughout New Mexico. Making properties look brand new since day one.
            </p>
            <div className="space-y-2">
              <a href="tel:5059102846" className="flex items-center gap-2 text-white/80 hover:text-teal text-sm transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                (505) 910-2846
              </a>
              <a href="mailto:rgps505@gmail.com" className="flex items-center gap-2 text-white/80 hover:text-teal text-sm transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                rgps505@gmail.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-white/40 mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map(link => (
                <li key={link}>
                  <button
                    onClick={() => scrollTo(link)}
                    className="text-white/70 hover:text-teal text-sm transition-colors"
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-white/40 mb-4">Service Areas</h4>
            <ul className="space-y-2.5">
              {serviceAreas.map(area => (
                <li key={area} className="text-white/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />
                  {area}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-white/40 mb-4">Get Started</h4>
            <p className="text-white/60 text-sm mb-4">Ready for a cleaner property? Get your free estimate today.</p>
            <button
              onClick={() => document.querySelector('#estimate')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full bg-teal hover:bg-teal-dark text-white font-bold py-3 rounded-xl text-sm transition-all mb-3"
            >
              Get Free Estimate
            </button>
            <a
              href="https://riograndepropertysolutions.booksy.com/a/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center border border-white/20 hover:border-teal text-white/70 hover:text-teal font-semibold py-3 rounded-xl text-sm transition-all"
            >
              Book Online
            </a>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-5">
              <a href="https://www.instagram.com/riograndepropertysolutions" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-teal flex items-center justify-center transition-all">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/riograndepropertysolutions" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-teal flex items-center justify-center transition-all">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.tiktok.com/@rgps505" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-teal flex items-center justify-center transition-all">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © 2026 Rio Grande Property Solutions. All Rights Reserved.
          </p>
          <p className="text-white/30 text-xs">
            Serving New Mexico with pride 🌵
          </p>
        </div>
      </div>
    </footer>
  );
}
